import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { FiCheck, FiX, FiCalendar, FiClock, FiUsers, FiRefreshCw, FiLogIn, FiLogOut, FiInbox, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import { SkeletonLine, SkeletonCircle, SkeletonBlock } from '../common/Skeleton.jsx';
import './AttendanceDashboard.css';

// Pure helpers — no component state, hoisted so identities stay stable across renders
// (also usable directly by the memoized row components below).
const getStatusBadge = (status, leaveType) => {
  switch (status) {
    case 'present':
      return <span className="badge badge-present"><FiCheck /> Present</span>;
    case 'on_leave':
      if (leaveType === 'paid') {
        return <span className="badge badge-leave-paid"><FiCalendar /> On Leave (Paid)</span>;
      }
      return <span className="badge badge-leave-unpaid"><FiCalendar /> On Leave (Unpaid)</span>;
    case 'off':
      return <span className="badge badge-off"><FiCalendar /> Weekend Off</span>;
    case 'absent':
    default:
      return <span className="badge badge-absent"><FiX /> Absent (Unpaid)</span>;
  }
};

// 75 -> "1h 15m", 20 -> "20m"
const formatLate = (minutes = 0) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

// Shown next to the status when the check-in was after 9:45 PM
const LateBadge = ({ record }) => (
  record.isLate ? (
    <span className="badge badge-late" title="Checked in after 9:45 PM — counted from 9:30 PM shift start">
      <FiAlertCircle /> Late {formatLate(record.lateMinutes)}
    </span>
  ) : null
);

// Status filter chips for the main table
const STATUS_FILTERS = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'present', label: 'Present', match: (r) => r.status === 'present' },
  { key: 'late', label: 'Late', match: (r) => r.isLate },
  { key: 'on_leave', label: 'On Leave', match: (r) => r.status === 'on_leave' },
  { key: 'absent', label: 'Absent', match: (r) => r.status === 'absent' }
];

const formatRole = (role) => {
  const map = {
    ceo: 'CEO', co_ceo: 'Co-CEO', company_manager: 'Company Manager',
    sales_head: 'Sales Head', sales_employee: 'Sales Employee',
    production_head: 'Production Head', production_employee: 'Production Employee',
    manager: 'Manager', employee: 'Employee', client: 'Client',
    attendance_manager: 'Attendance Officer'
  };
  return map[role] || role;
};

const formatTime = (time) => {
  // new Date(null) is 1970-01-01 (shows as "05:00 AM" in PKT) — show a dash instead
  if (!time) return '-';
  return new Date(time).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Memoized summary card — the summary grid only has 4 entries, but this keeps
// each card from re-rendering when unrelated dashboard state changes.
const SummaryCard = memo(function SummaryCard({ variant, icon, value, label }) {
  return (
    <div className={`summary-card ${variant}`}>
      <div className="summary-icon">{icon}</div>
      <div className="summary-info">
        <div className="summary-value">{value}</div>
        <div className="summary-label">{label}</div>
      </div>
    </div>
  );
});

// Memoized row for the main attendance table — keeps unrelated dashboard re-renders
// (e.g. a future search/filter input) from re-rendering every row.
const AttendanceRow = memo(function AttendanceRow({ record, canMark, loadingAction, onCheckIn, onCheckOut, onViewMonthly }) {
  const isCheckingIn = loadingAction === 'checkin';
  const isCheckingOut = loadingAction === 'checkout';
  const rowBusy = Boolean(loadingAction);

  return (
    <tr>
      <td className="cell-employee">
        <div className="employee-cell">
          {record.user.profilePicture ? (
            <img src={record.user.profilePicture} alt="Profile" className="employee-avatar" />
          ) : (
            <div className="employee-avatar">
              {record.user.firstName?.[0]}{record.user.lastName?.[0]}
            </div>
          )}
          <div className="employee-info">
            <div className="employee-name">
              {record.user.firstName} {record.user.lastName}
            </div>
            <div className="employee-role">{formatRole(record.user.role)}</div>
          </div>
        </div>
      </td>
      <td className="cell-dept">
        <span className="dept-badge" data-dept={record.user.department?.toLowerCase()}>
          {record.user.department?.toUpperCase()}
        </span>
      </td>
      <td className="cell-status">
        <div className="status-cell">
          {getStatusBadge(record.status, record.leaveType)}
          <LateBadge record={record} />
        </div>
      </td>
      {/* data-label is shown as the field caption in the mobile card layout */}
      <td className="time-cell" data-label="Check In">{formatTime(record.checkInTime)}</td>
      <td className="time-cell" data-label="Check Out">
        {formatTime(record.checkOutTime)}
        {record.autoCheckedOut && <span className="badge-auto" title="Auto checked-out at shift end">auto</span>}
      </td>
      <td className="hours-cell" data-label="Hours">
        {record.workingHours > 0 ? (
          <span className="hours-value">
            <FiClock /> {record.workingHours.toFixed(1)}h
          </span>
        ) : (
          '-'
        )}
      </td>
      <td className="actions-cell">
        <div className="action-buttons">
          {canMark && record.status !== 'on_leave' && (
            <>
              <button
                className="btn-action btn-check-in"
                onClick={() => onCheckIn(record.user._id)}
                disabled={record.checkInTime || rowBusy}
                title={record.checkInTime ? 'Already checked in' : 'Check In'}
              >
                {isCheckingIn ? <span className="btn-spinner" /> : <FiLogIn />}
              </button>
              <button
                className="btn-action btn-check-out"
                onClick={() => onCheckOut(record.user._id)}
                disabled={!record.checkInTime || record.checkOutTime || rowBusy}
                title={!record.checkInTime ? 'Check in first' : record.checkOutTime ? 'Already checked out' : 'Check Out'}
              >
                {isCheckingOut ? <span className="btn-spinner" /> : <FiLogOut />}
              </button>
            </>
          )}
          <button
            className="btn-action btn-view-monthly"
            onClick={() => onViewMonthly(record.user)}
            title="View Monthly Attendance"
          >
            <FiCalendar />
          </button>
        </div>
      </td>
    </tr>
  );
});

// Memoized row for the monthly-breakdown modal table.
const MonthlyAttendanceRow = memo(function MonthlyAttendanceRow({ record }) {
  return (
    <tr>
      <td className="cell-date">{new Date(record.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: process.env.REACT_APP_OFFICE_TIMEZONE || 'Asia/Karachi' })}</td>
      <td className="cell-status">
        <div className="status-cell">
          {getStatusBadge(record.status, record.leaveType)}
          <LateBadge record={record} />
        </div>
      </td>
      <td className="time-cell" data-label="Check In">{formatTime(record.checkInTime)}</td>
      <td className="time-cell" data-label="Check Out">
        {formatTime(record.checkOutTime)}
        {record.autoCheckedOut && <span className="badge-auto" title="Auto checked-out at shift end">auto</span>}
      </td>
      <td className="hours-cell" data-label="Hours">
        {record.workingHours > 0 ? (
          <span className="hours-value">
            <FiClock /> {record.workingHours.toFixed(1)}h
          </span>
        ) : (
          '-'
        )}
      </td>
    </tr>
  );
});

// Skeleton row mirroring the real attendance table's column shapes (avatar +
// name, badge pills, time text, action buttons) instead of generic bars.
const AttendanceRowSkeleton = memo(function AttendanceRowSkeleton() {
  return (
    <tr aria-hidden="true">
      <td className="cell-employee">
        <div className="employee-cell">
          <SkeletonCircle size={40} />
          <div className="employee-info">
            <SkeletonLine width="70%" height={13} style={{ marginBottom: 6 }} />
            <SkeletonLine width="45%" height={11} />
          </div>
        </div>
      </td>
      <td className="cell-dept"><SkeletonBlock width={70} height={22} radius={12} /></td>
      <td className="cell-status"><SkeletonBlock width={110} height={24} radius={16} /></td>
      <td className="time-cell"><SkeletonLine width={50} height={12} /></td>
      <td className="time-cell"><SkeletonLine width={50} height={12} /></td>
      <td className="hours-cell"><SkeletonLine width={40} height={12} /></td>
      <td className="actions-cell">
        <div className="action-buttons">
          <SkeletonBlock width={34} height={34} radius={8} />
          <SkeletonBlock width={34} height={34} radius={8} />
          <SkeletonBlock width={34} height={34} radius={8} />
        </div>
      </td>
    </tr>
  );
});

const AttendanceDashboardSkeleton = memo(function AttendanceDashboardSkeleton() {
  return (
    <div className="attendance-dashboard" aria-hidden="true">
      <div className="dashboard-header">
        <div className="header-title">
          <h1><FiUsers /> Employee Attendance</h1>
          <SkeletonLine width={220} height={13} style={{ marginTop: 8 }} />
        </div>
        <SkeletonBlock width={120} height={40} radius={10} />
      </div>

      <div className="summary-grid">
        {Array.from({ length: 5 }).map((_, i) => (
          <div className="summary-card" key={i}>
            <SkeletonBlock width={50} height={50} radius={12} />
            <div className="summary-info">
              <SkeletonLine width={40} height={22} style={{ marginBottom: 8 }} />
              <SkeletonLine width={80} height={11} />
            </div>
          </div>
        ))}
      </div>

      <div className="attendance-table-container">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Status</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Hours</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 8 }).map((_, i) => (
              <AttendanceRowSkeleton key={i} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

// Roles that may check in/out on behalf of other users (others can only mark their own row)
const MARK_OTHERS_ROLES = ['ceo', 'co_ceo', 'company_manager', 'attendance_manager'];

// `onBack` (optional) shows a button that returns to the user's own attendance screen
const AttendanceDashboard = ({ onBack }) => {
  const { user } = useAuthStore();
  const currentUserId = String(user?._id || user?.id || '');
  const canMarkOthers = MARK_OTHERS_ROLES.includes(user?.role);
  const [attendanceData, setAttendanceData] = useState([]);
  const [workDate, setWorkDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showMonthlyModal, setShowMonthlyModal] = useState(false);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [modalStartDate, setModalStartDate] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [modalEndDate, setModalEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  // Tracks which async action (checkin/checkout) is in flight per userId, so
  // only the button that was actually clicked shows a spinner and disables.
  const [actionLoading, setActionLoading] = useState({});
  const [summary, setSummary] = useState({
    present: 0,
    late: 0,
    absent: 0,
    onLeave: 0,
    total: 0
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthlyLateOnly, setMonthlyLateOnly] = useState(false);

  const fetchAttendanceData = useCallback(async () => {
    try {
      const response = await api.get('/attendance/all-status');
      const data = response.data.attendanceStatus;
      setAttendanceData(data);
      if (response.data.date) setWorkDate(response.data.date);

      // Calculate summary
      const summary = {
        present: data.filter(d => d.status === 'present').length,
        late: data.filter(d => d.isLate).length,
        absent: data.filter(d => d.status === 'absent').length,
        onLeave: data.filter(d => d.status === 'on_leave').length,
        total: data.length
      };
      setSummary(summary);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttendanceData();
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchAttendanceData, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAttendanceData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchAttendanceData();
    setRefreshing(false);
  }, [fetchAttendanceData]);

  const fetchMonthlyAttendance = useCallback(async (userId, startDate, endDate) => {
    setLoadingMonthly(true);
    try {
      const start = startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      const end = endDate || new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
      const response = await api.get(`/attendance/history?userId=${userId}&startDate=${start.toISOString()}&endDate=${end.toISOString()}&limit=100`);
      // backend returns 'attendances', normalize to 'attendance'
      setMonthlyData({
        ...response.data,
        attendance: response.data.attendances || response.data.attendance || []
      });
    } catch (error) {
      console.error('Failed to fetch monthly attendance:', error);
    } finally {
      setLoadingMonthly(false);
    }
  }, []);

  const handleViewMonthly = useCallback((employee) => {
    setSelectedEmployee(employee);
    setMonthlyLateOnly(false);
    setShowMonthlyModal(true);
    fetchMonthlyAttendance(employee._id);
  }, [fetchMonthlyAttendance]);

  const handleCheckIn = useCallback(async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'checkin' }));
    try {
      await api.post('/attendance/check-in', { userId });
      await fetchAttendanceData();
    } catch (error) {
      console.error('Check-in failed:', error);
      alert(error.response?.data?.message || 'Failed to check in');
    } finally {
      setActionLoading(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }
  }, [fetchAttendanceData]);

  const handleCheckOut = useCallback(async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'checkout' }));
    try {
      await api.post('/attendance/check-out', { userId });
      await fetchAttendanceData();
    } catch (error) {
      console.error('Check-out failed:', error);
      alert(error.response?.data?.message || 'Failed to check out');
    } finally {
      setActionLoading(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }
  }, [fetchAttendanceData]);

  const monthlyStats = useMemo(() => {
    if (!monthlyData?.attendance) return null;
    const attendance = monthlyData.attendance;
    const late = attendance.filter(a => a.isLate);
    return {
      present: attendance.filter(a => a.status === 'present').length,
      late: late.length,
      totalLate: formatLate(late.reduce((sum, a) => sum + (a.lateMinutes || 0), 0)),
      absent: attendance.filter(a => a.status === 'absent').length,
      onLeave: attendance.filter(a => a.status === 'on_leave').length,
      totalHours: attendance.reduce((sum, a) => sum + (a.workingHours || 0), 0).toFixed(1)
    };
  }, [monthlyData]);

  const filteredAttendance = useMemo(() => {
    const filter = STATUS_FILTERS.find(f => f.key === statusFilter) || STATUS_FILTERS[0];
    return attendanceData.filter(filter.match);
  }, [attendanceData, statusFilter]);

  const monthlyRows = useMemo(() => {
    const rows = monthlyData?.attendance || [];
    return monthlyLateOnly ? rows.filter(a => a.isLate) : rows;
  }, [monthlyData, monthlyLateOnly]);

  if (loading) {
    return <AttendanceDashboardSkeleton />;
  }

  return (
    <div className="attendance-dashboard">
      <div className="dashboard-header">
        <div className="header-title">
          <h1><FiUsers /> Employee Attendance</h1>
          <p>Current shift overview - {new Date(workDate || Date.now()).toLocaleDateString('en-US', {
            timeZone: process.env.REACT_APP_OFFICE_TIMEZONE || 'Asia/Karachi',
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}</p>
        </div>
        <div className="header-actions">
          {onBack && (
            <button className="refresh-btn" onClick={onBack}>
              <FiArrowLeft /> My Attendance
            </button>
          )}
          <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <span className="btn-spinner btn-spinner-dark" /> : <FiRefreshCw />}
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <SummaryCard variant="total" icon={<FiUsers />} value={summary.total} label="Total Employees" />
        <SummaryCard variant="present" icon={<FiCheck />} value={summary.present} label="Present" />
        <SummaryCard variant="late" icon={<FiAlertCircle />} value={summary.late} label="Late (after 9:45)" />
        <SummaryCard variant="on-leave" icon={<FiCalendar />} value={summary.onLeave} label="On Leave" />
        <SummaryCard variant="absent" icon={<FiX />} value={summary.absent} label="Absent" />
      </div>

      {/* Status filter */}
      <div className="status-filter" role="group" aria-label="Filter by status">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            className={`filter-chip ${statusFilter === f.key ? 'active' : ''} ${f.key}`}
            onClick={() => setStatusFilter(f.key)}
          >
            {f.label}
            <span className="filter-count">{attendanceData.filter(f.match).length}</span>
          </button>
        ))}
      </div>

      {/* Attendance Table */}
      <div className="attendance-table-container">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Status</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Hours</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAttendance.map((record) => (
              <AttendanceRow
                key={record.user._id}
                record={record}
                canMark={canMarkOthers || String(record.user._id) === currentUserId}
                loadingAction={actionLoading[record.user._id]}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
                onViewMonthly={handleViewMonthly}
              />
            ))}
          </tbody>
        </table>

        {filteredAttendance.length === 0 && (
          <div className="empty-state">
            <FiInbox className="empty-state-icon" />
            <p>{attendanceData.length === 0 ? 'No attendance data available' : 'No employees match this filter'}</p>
          </div>
        )}
      </div>

      {/* Monthly Attendance Modal */}
      {showMonthlyModal && (
        <div className="modal-overlay attendance-modal-overlay" onClick={() => setShowMonthlyModal(false)}>
          <div className="modal-content attendance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FiCalendar /> Monthly Attendance — {selectedEmployee?.firstName} {selectedEmployee?.lastName}
              </h2>
              <button className="modal-close" onClick={() => setShowMonthlyModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-date-filter">
              <label>From</label>
              <input type="date" value={modalStartDate} onChange={e => setModalStartDate(e.target.value)} />
              <label>To</label>
              <input type="date" value={modalEndDate} onChange={e => setModalEndDate(e.target.value)} />
              <button className="refresh-btn" style={{padding:'8px 18px', fontSize:'0.85rem'}} onClick={() => fetchMonthlyAttendance(selectedEmployee._id, new Date(modalStartDate), new Date(modalEndDate))}>
                Apply
              </button>
              <label className="late-only-toggle">
                <input type="checkbox" checked={monthlyLateOnly} onChange={e => setMonthlyLateOnly(e.target.checked)} />
                Late days only
              </label>
            </div>
            <div className="modal-body">
              {loadingMonthly ? (
                <>
                  <div className="monthly-summary">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div className="monthly-stat" key={i}>
                        <SkeletonCircle size={22} />
                        <SkeletonLine width={90} height={13} />
                      </div>
                    ))}
                  </div>
                  <div className="monthly-table-container">
                    <table className="monthly-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Check In</th>
                          <th>Check Out</th>
                          <th>Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: 6 }).map((_, i) => (
                          <tr key={i} aria-hidden="true">
                            <td><SkeletonLine width={80} height={12} /></td>
                            <td><SkeletonBlock width={100} height={22} radius={16} /></td>
                            <td><SkeletonLine width={50} height={12} /></td>
                            <td><SkeletonLine width={50} height={12} /></td>
                            <td><SkeletonLine width={40} height={12} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : monthlyData && monthlyData.attendance ? (
                <>
                  <div className="monthly-summary">
                    <div className="monthly-stat present-stat">
                      <div className="monthly-stat-icon"><FiCheck /></div>
                      <div className="monthly-stat-value">{monthlyStats.present}</div>
                      <div className="monthly-stat-label">Present</div>
                    </div>
                    <div className="monthly-stat late-stat" title={`Total late time: ${monthlyStats.totalLate}`}>
                      <div className="monthly-stat-icon"><FiAlertCircle /></div>
                      <div className="monthly-stat-value">{monthlyStats.late}</div>
                      <div className="monthly-stat-label">Late · {monthlyStats.totalLate}</div>
                    </div>
                    <div className="monthly-stat absent-stat">
                      <div className="monthly-stat-icon"><FiX /></div>
                      <div className="monthly-stat-value">{monthlyStats.absent}</div>
                      <div className="monthly-stat-label">Absent</div>
                    </div>
                    <div className="monthly-stat leave-stat">
                      <div className="monthly-stat-icon"><FiCalendar /></div>
                      <div className="monthly-stat-value">{monthlyStats.onLeave}</div>
                      <div className="monthly-stat-label">On Leave</div>
                    </div>
                    <div className="monthly-stat hours-stat">
                      <div className="monthly-stat-icon"><FiClock /></div>
                      <div className="monthly-stat-value">{monthlyStats.totalHours}h</div>
                      <div className="monthly-stat-label">Total Hours</div>
                    </div>
                  </div>

                  <div className="monthly-table-container">
                    <table className="monthly-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Check In</th>
                          <th>Check Out</th>
                          <th>Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyRows.map((record) => (
                          <MonthlyAttendanceRow key={record._id} record={record} />
                        ))}
                        {monthlyRows.length === 0 && (
                          <tr>
                            <td colSpan={5} className="monthly-empty">
                              {monthlyLateOnly ? 'No late days in this range' : 'No records in this range'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <FiInbox className="empty-state-icon" />
                  <p>No attendance data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceDashboard;
